import React, { useState, useEffect } from 'react';
import { content as contentApi } from '../../api/client';
import { FileText, Plus, Loader2, CheckCircle2, XCircle, ExternalLink } from 'lucide-react';

const Badge = ({ children, variant = "default" }) => {
  const variants = {
    default: "bg-stone-100 text-stone-600 dark:bg-stone-800 dark:text-stone-300",
    success: "bg-emerald-50 text-emerald-700",
    warning: "bg-amber-50 text-amber-700",
    danger: "bg-rose-50 text-rose-700",
  };
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium ${variants[variant]}`}>{children}</span>;
};

export default function ContentPage({ activeBrandId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newUrl, setNewUrl] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [adding, setAdding] = useState(false);

  const loadData = async () => {
    if (!activeBrandId) return;
    setLoading(true);
    try {
      const result = await contentApi.list(activeBrandId);
      setData(result);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, [activeBrandId]);

  const handleAdd = async () => {
    if (!newUrl.trim()) return;
    setAdding(true);
    try {
      await contentApi.add(activeBrandId, { url: newUrl, title: newTitle });
      setNewUrl('');
      setNewTitle('');
      setShowAdd(false);
      loadData();
    } catch (e) { console.error(e); }
    finally { setAdding(false); }
  };

  const scoreColor = (score) => score >= 70 ? 'text-emerald-600' : score >= 40 ? 'text-amber-600' : 'text-rose-600';

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <span className="text-xs font-medium tracking-widest uppercase block mb-2 text-stone-500">Optimization</span>
          <h1 className="font-serif text-2xl md:text-3xl text-stone-900 dark:text-white">Content</h1>
        </div>
        <button onClick={() => setShowAdd(true)} className="px-4 py-1.5 text-sm bg-stone-900 text-white dark:bg-white dark:text-stone-900 flex items-center gap-1">
          <Plus className="w-4 h-4" /> Add Page
        </button>
      </div>

      {/* Stats */}
      {data?.stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 p-4">
            <span className="text-xs font-medium tracking-widest uppercase text-stone-500">Pages</span>
            <div className="font-serif text-2xl text-stone-900 dark:text-white mt-1">{data.stats.totalPages}</div>
          </div>
          <div className="bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 p-4">
            <span className="text-xs font-medium tracking-widest uppercase text-stone-500">Avg Score</span>
            <div className="font-serif text-2xl text-stone-900 dark:text-white mt-1">{data.stats.avgScore}</div>
          </div>
          <div className="bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 p-4">
            <span className="text-xs font-medium tracking-widest uppercase text-stone-500">With Capsule</span>
            <div className="font-serif text-2xl text-stone-900 dark:text-white mt-1">{data.stats.withCapsule}</div>
          </div>
          <div className="bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 p-4">
            <span className="text-xs font-medium tracking-widest uppercase text-stone-500">With Schema</span>
            <div className="font-serif text-2xl text-stone-900 dark:text-white mt-1">{data.stats.withSchema}</div>
          </div>
        </div>
      )}

      {/* Add Modal */}
      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowAdd(false)} />
          <div className="relative bg-white dark:bg-stone-900 w-full max-w-lg border border-stone-200 dark:border-stone-700 shadow-xl p-6">
            <h2 className="font-serif text-xl text-stone-900 dark:text-white mb-4">Add Content Page</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-1">URL</label>
                <input value={newUrl} onChange={(e) => setNewUrl(e.target.value)}
                  className="w-full px-3 py-2 border border-stone-300 dark:border-stone-600 bg-white dark:bg-stone-800 text-stone-900 dark:text-white" placeholder="https://..." />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-1">Title (optional)</label>
                <input value={newTitle} onChange={(e) => setNewTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-stone-300 dark:border-stone-600 bg-white dark:bg-stone-800 text-stone-900 dark:text-white" placeholder="Page title" />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setShowAdd(false)} className="px-4 py-2 text-sm border border-stone-200 text-stone-600">Cancel</button>
              <button onClick={handleAdd} disabled={adding} className="px-4 py-2 text-sm bg-stone-900 text-white flex items-center gap-1">
                {adding && <Loader2 className="w-4 h-4 animate-spin" />} Add
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-stone-400" /></div>
      ) : (
        <div className="space-y-3">
          {(data?.pages || []).length === 0 ? (
            <div className="bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 p-8 text-center text-stone-500">No content pages added yet.</div>
          ) : (
            (data?.pages || []).map((page) => (
              <div key={page.page_id} className="bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 p-4 hover:border-stone-400 transition-colors">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-stone-900 dark:text-white truncate">{page.title || page.url}</p>
                      <a href={page.url} target="_blank" rel="noopener noreferrer"><ExternalLink className="w-4 h-4 text-stone-400" /></a>
                    </div>
                    <p className="text-sm text-stone-500 truncate mt-1">{page.url}</p>
                    <div className="flex items-center gap-3 mt-2">
                      <span className="text-xs text-stone-400">{page.word_count?.toLocaleString()} words</span>
                      <span className="flex items-center gap-1 text-xs">
                        {page.has_capsule ? <CheckCircle2 className="w-3 h-3 text-emerald-500" /> : <XCircle className="w-3 h-3 text-stone-300" />}
                        Capsule
                      </span>
                      <span className="flex items-center gap-1 text-xs">
                        {page.has_schema ? <CheckCircle2 className="w-3 h-3 text-emerald-500" /> : <XCircle className="w-3 h-3 text-stone-300" />}
                        Schema
                      </span>
                      <Badge variant={page.priority === 'HIGH' ? 'danger' : page.priority === 'MEDIUM' ? 'warning' : 'default'}>{page.priority}</Badge>
                    </div>
                  </div>
                  <div className="text-center flex-shrink-0">
                    <div className={`font-serif text-2xl ${scoreColor(page.score)}`}>{page.score}</div>
                    <div className="text-xs text-stone-400">Score</div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
